import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load components for better performance
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage'));
const TrackedTasksPage = React.lazy(() => import('./pages/TrackedTasksPage'));
const TaskDetailPage = React.lazy(() => import('./pages/TaskDetailPage'));
const LeaderboardPage = React.lazy(() => import('./pages/LeaderboardPage'));
const StatsPage = React.lazy(() => import('./pages/StatsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const WalletPage = React.lazy(() => import('./pages/WalletPage'));
const FaucetPage = React.lazy(() => import('./pages/FaucetPage'));
const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const ChatComponent = React.lazy(() => import('./components/chat/ChatComponent'));

// Layout components
const Layout = React.lazy(() => import('./components/Layout'));

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10B981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#EF4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                
                {/* Protected Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/discover" element={
                  <ProtectedRoute>
                    <Layout>
                      <DiscoverPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/tracked-tasks" element={
                  <ProtectedRoute>
                    <Layout>
                      <TrackedTasksPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/task/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <TaskDetailPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/leaderboard" element={
                  <ProtectedRoute>
                    <Layout>
                      <LeaderboardPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/stats" element={
                  <ProtectedRoute>
                    <Layout>
                      <StatsPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Layout>
                      <ProfilePage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/wallet" element={
                  <ProtectedRoute>
                    <Layout>
                      <WalletPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/faucets" element={
                  <ProtectedRoute>
                    <Layout>
                      <FaucetPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Admin Routes */}
                <Route path="/admin" element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                } />
                
                {/* Default redirect */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              {/* Global Chat Component */}
              <ChatComponent />
            </Suspense>
          </div>
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
